import { BRANCHES } from "../lib/bakeryData";
import { MapPin, Phone, Clock, Calendar, ExternalLink, MessageCircle } from "lucide-react";

const buildWhatsAppLink = (number, branchName) => {
    const msg = encodeURIComponent(
        `Hi! I'd like to place an order at The Baker's Basket — ${branchName}.`
    );
    return `https://wa.me/${number}?text=${msg}`;
};

const Branches = () => {
    return (
        <main data-testid="branches-page" className="bg-[#FAF8F5] pt-32 pb-20">
            <section className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-3xl mx-auto">
                    <p className="font-handwriting text-3xl text-[#C5832B]">Find us</p>
                    <h1 className="font-serif text-5xl md:text-6xl text-[#3E2723] mt-3 leading-tight">
                        Our Branches
                    </h1>
                    <p className="text-base text-[#6D4C41] mt-5 leading-relaxed">
                        Three little homes in Chhattisgarh — each with its own regulars,
                        its own quirks, and the same warm bread.
                    </p>
                </div>

                <div className="mt-16 space-y-10">
                    {BRANCHES.map((b) => {
                        const hasDelivery = Boolean(b.zomatoUrl || b.swiggyUrl);
                        return (
                            <article
                                key={b.id}
                                data-testid={`branch-card-${b.id}`}
                                className="bg-white border border-[#E8D8C8] rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-500 lg:flex"
                            >
                                <div className="lg:w-1/2 aspect-[4/3] lg:aspect-auto overflow-hidden bg-[#F3EBE1] relative">
                                    {b.mapsEmbed ? (
                                        <iframe
                                            src={b.mapsEmbed}
                                            title={`${b.name} location map`}
                                            data-testid={`branch-${b.id}-map-embed`}
                                            className="w-full h-full border-0 min-h-[360px]"
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            allowFullScreen
                                        />
                                    ) : (
                                        <img
                                            src={b.image}
                                            alt={b.name}
                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        />
                                    )}
                                </div>

                                <div className="p-8 md:p-10 lg:w-1/2">
                                    <p className="font-handwriting text-2xl text-[#C5832B] leading-none">
                                        {b.tagline}
                                    </p>
                                    <h2 className="font-serif text-4xl text-[#3E2723] mt-2">
                                        {b.name}
                                    </h2>

                                    <ul className="mt-6 space-y-3 text-[#6D4C41] text-sm">
                                        <li className="flex items-start gap-3">
                                            <MapPin className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                            <span>{b.address}</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <Phone className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                            <a
                                                href={`tel:${b.phone.replace(/\s/g, "")}`}
                                                data-testid={`branch-${b.id}-phone`}
                                                className="hover:text-[#C5832B] transition-colors"
                                            >
                                                {b.phone}
                                            </a>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <Clock className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                            <span>{b.hours}</span>
                                        </li>
                                        <li className="flex items-start gap-3">
                                            <Calendar className="w-4 h-4 text-[#C5832B] mt-0.5 flex-shrink-0" />
                                            <span>Opened: {b.opened}</span>
                                        </li>
                                    </ul>

                                    {/* Primary WhatsApp order CTA */}
                                    {b.whatsapp && (
                                        <a
                                            href={buildWhatsAppLink(b.whatsapp, b.name)}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`branch-${b.id}-whatsapp`}
                                            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1ebe5b] transition-all hover:scale-[1.01]"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                            Order on WhatsApp
                                        </a>
                                    )}

                                    {/* Delivery partner buttons — only when available */}
                                    {hasDelivery && (
                                        <div className="mt-3">
                                            <p className="text-xs uppercase tracking-[0.18em] text-[#6D4C41] mb-2.5">
                                                Or order via
                                            </p>
                                            <div className="grid grid-cols-2 gap-3">
                                                {b.zomatoUrl && (
                                                    <a
                                                        href={b.zomatoUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        data-testid={`branch-${b.id}-zomato`}
                                                        className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#E23744] text-white text-sm hover:bg-[#c12d39] transition-colors"
                                                    >
                                                        Zomato
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                {b.swiggyUrl && (
                                                    <a
                                                        href={b.swiggyUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        data-testid={`branch-${b.id}-swiggy`}
                                                        className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FC8019] text-white text-sm hover:bg-[#e57315] transition-colors"
                                                    >
                                                        Swiggy
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <a
                                        href={b.mapsUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`branch-${b.id}-directions`}
                                        className="inline-flex items-center gap-2 mt-5 text-sm uppercase tracking-wide text-[#3E2723] hover:text-[#C5832B] transition-colors"
                                    >
                                        Get directions
                                        <ExternalLink className="w-3.5 h-3.5" />
                                    </a>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </section>
        </main>
    );
};

export default Branches;
